import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Lock, RefreshCw, CheckCircle, IndianRupee, Clock, ArrowLeft, Layers, AlertTriangle,
  FileText, Trash2, CalendarClock,
} from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import {
  listOrgPeriods, generateRun, getRun, settleRun, recordPayout, listPayouts, getRunAudit,
  setOrgCadence, deleteDraftRun,
  OrgPeriod, Cadence, RunSummary, RunPartyRollup, RunProfileBreakdown, Payout, AuditEntry,
} from "../../../api/settlement";
import "../shared/maintenance-tables.scss";
import { useNotice } from "../shared/useNotice";

// ---- styles (maintenance aesthetic) ----------------------------------------
const card: React.CSSProperties = { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 10, padding: 16, marginBottom: 16 };
const pillBase: React.CSSProperties = { display: "inline-block", padding: "2px 9px", borderRadius: 999, fontSize: 12, fontWeight: 600 };
const pillGreen: React.CSSProperties = { ...pillBase, background: "#e8f7ef", color: "#1a7f4b" };
const pillAmber: React.CSSProperties = { ...pillBase, background: "#fff7e6", color: "#b9770e" };
const pillGrey: React.CSSProperties = { ...pillBase, background: "#eef0f2", color: "#555" };
const pillBlue: React.CSSProperties = { ...pillBase, background: "#eef2fb", color: "#2563eb" };
const primaryBtn: React.CSSProperties = { background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 };
const dangerBtn: React.CSSProperties = { ...primaryBtn, background: "#c0392b" };
const ghostBtn: React.CSSProperties = { background: "#f1f3f5", color: "#333", border: "1px solid #e0e0e0", borderRadius: 8, padding: "6px 12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 };
const th: React.CSSProperties = { borderBottom: "1px solid #e0e0e0", padding: "8px 10px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#777", textTransform: "uppercase", letterSpacing: ".03em" };
const thR: React.CSSProperties = { ...th, textAlign: "right" };
const td: React.CSSProperties = { borderBottom: "1px solid #f0f0f0", padding: "8px 10px", color: "#333" };
const tdR: React.CSSProperties = { ...td, textAlign: "right" };
const input: React.CSSProperties = { border: "1px solid #d0d0d0", borderRadius: 6, padding: "6px 8px", fontSize: 13 };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtPeriod = (key: string) => {
  if (key.includes("-Q")) { const [y, q] = key.split("-Q"); return `Q${q} ${y}`; }
  const [y, m] = key.split("-"); const mi = Number(m) - 1;
  return mi >= 0 && mi < 12 ? `${MONTHS[mi]} ${y}` : key;
};
const inr = (n: number) => `₹${(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const num = (n: number) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
const fmtDate = (d: string | null) => { if (!d) return "—"; try { return new Date(d).toLocaleString(); } catch { return d; } };
const errMsg = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};
const GST_LABEL: Record<string, string> = { exempt: "Exempt", gst_18_forward: "18% forward", gst_18_rcm: "18% RCM" };

// ---- payout mini-form -------------------------------------------------------
const PayoutForm: React.FC<{ party: RunPartyRollup; onSubmit: (a: number, r: string, n: string) => Promise<void> }> = ({ party, onSubmit }) => {
  const due = Math.max(0, party.total_payable - party.paid);
  const [amount, setAmount] = useState(String(Math.round(due)));
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useNotice("error", "");
  const submit = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setError("Enter a positive amount"); return; }
    setSaving(true); setError("");
    try { await onSubmit(amt, reference.trim(), note.trim()); setReference(""); setNote(""); }
    catch (err) { setError(errMsg(err, "Failed to record payout")); }
    finally { setSaving(false); }
  };
  if (party.fully_paid) return <span style={pillGreen}>Paid</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
      <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ ...input, width: 100 }} placeholder="Amount" />
      <input value={reference} onChange={(e) => setReference(e.target.value)} style={{ ...input, width: 120 }} placeholder="Reference" />
      <input value={note} onChange={(e) => setNote(e.target.value)} style={{ ...input, width: 130 }} placeholder="Note" />
      <button style={{ ...primaryBtn, padding: "6px 10px", opacity: saving ? 0.6 : 1 }} onClick={submit} disabled={saving}>
        {saving ? "Saving…" : "Record payout"}
      </button>
      {error && <span style={{ color: "#c0392b", fontSize: 12 }}>{error}</span>}
    </div>
  );
};

// ---- per-tier breakdown -----------------------------------------------------
const TierBreakdown: React.FC<{ profiles: RunProfileBreakdown[] }> = ({ profiles }) => {
  if (!profiles.length) return null;
  return (
    <div style={card}>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 600, color: "#333" }}>
        <Layers size={16} style={{ verticalAlign: "-2px" }} /> Rate-card breakdown by tier
      </h3>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#777" }}>
        How each tier's taxable price is split. Energy × ₹/kWh = amount; GST is the overlay per line.
      </p>
      {profiles.map((p, i) => (
        <div key={p.profile_id ?? i} style={{ border: "1px solid #ececec", borderRadius: 8, marginBottom: 12 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, padding: "10px 12px", background: "#fafbfc", borderBottom: "1px solid #ececec" }}>
            <b style={{ color: "#333" }}>{p.station_name || "—"}</b>
            <span style={pillGrey}>{p.charger_type || "AC"}</span>
            {p.price_org_name && <span style={pillBlue}>{p.price_org_name} tier</span>}
            <span style={{ marginLeft: "auto", fontSize: 13, color: "#555" }}>
              {num(p.kwh)} kWh · taxable <b>₹{(p.taxable_unit_price ?? 0).toFixed(2)}/kWh</b>
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={th}>Kind</th><th style={th}>Party</th><th style={th}>Label</th>
                  <th style={thR}>₹/kWh</th><th style={thR}>Amount</th><th style={th}>GST</th><th style={thR}>GST ₹</th>
                </tr>
              </thead>
              <tbody>
                {p.lines.map((l, j) => (
                  <tr key={j}>
                    <td style={td}>
                      <span style={l.line_kind === "consumable" ? pillAmber : pillBlue}>
                        {l.line_kind === "consumable" ? "Consumable" : "Share"}
                      </span>
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>{l.party_name || "—"}</td>
                    <td style={td}>{l.label}</td>
                    <td style={tdR}>{l.rate_per_kwh.toFixed(2)}</td>
                    <td style={{ ...tdR, fontWeight: 600 }}>{inr(l.amount)}</td>
                    <td style={td}>{GST_LABEL[l.gst_treatment] || l.gst_treatment}</td>
                    <td style={tdR}>{l.gst_amount ? inr(l.gst_amount) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

// ---- main -------------------------------------------------------------------
const SettlementOrgRuns: React.FC = () => {
  const { orgId: orgIdRaw } = useParams();
  const orgId = Number(orgIdRaw);
  const canEdit = canEditMaintenance();

  const [orgName, setOrgName] = useState("");
  const [cadence, setCadence] = useState<Cadence>("quarterly");
  const [periods, setPeriods] = useState<OrgPeriod[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [run, setRun] = useState<RunSummary | null>(null);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useNotice("error", "");
  const [message, setMessage] = useNotice("success", "");

  const selectedPeriod = periods.find((p) => p.period_key === selected) || null;
  const isSettled = run?.status === "settled";
  const isDraft = run?.status === "draft";

  const loadPeriods = useCallback(async () => {
    const res = await listOrgPeriods(orgId);
    setOrgName(res.org_name);
    setCadence(res.cadence);
    setPeriods(res.periods);
    return res.periods;
  }, [orgId]);

  const loadSettledDetail = useCallback(async (periodKey: string) => {
    try {
      const [p, a] = await Promise.all([listPayouts(orgId, periodKey), getRunAudit(orgId, periodKey)]);
      setPayouts(p); setAudit(a);
    } catch (err) { console.error("Failed to load payouts/audit:", err); }
  }, [orgId]);

  const openPeriod = useCallback(async (periodKey: string, ps?: OrgPeriod[]) => {
    setSelected(periodKey);
    setRun(null); setPayouts([]); setAudit([]); setError(""); setMessage("");
    const p = (ps || periods).find((x) => x.period_key === periodKey);
    if (!p || p.status === "none") return;
    setBusy(true);
    try {
      const summary = await getRun(orgId, periodKey);
      setRun(summary);
      if (summary.status === "settled") await loadSettledDetail(periodKey);
    } catch (err) { setError(errMsg(err, "Failed to load run")); }
    finally { setBusy(false); }
  }, [orgId, periods, loadSettledDetail]);

  useEffect(() => {
    if (!Number.isFinite(orgId)) { setError("Invalid org"); setLoading(false); return; }
    let active = true;
    setLoading(true);
    loadPeriods()
      .then((ps) => {
        if (!active) return;
        const pick = ps.find((p) => p.completed) || ps[0];
        if (pick) void openPeriod(pick.period_key, ps);
      })
      .catch((err) => { if (active) setError(errMsg(err, "Failed to load periods")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const compute = async () => {
    if (!selected) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const summary = await generateRun(orgId, selected);
      setRun(summary);
      await loadPeriods();
      if (summary.status === "settled") await loadSettledDetail(selected);
    } catch (err) { setError(errMsg(err, "Failed to compute run")); }
    finally { setBusy(false); }
  };

  const handleSettle = async () => {
    if (!run || !selected) return;
    if (!window.confirm(`Settle ${fmtPeriod(run.period_key)} for ${orgName}? This permanently freezes the figures.`)) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const settled = await settleRun(orgId, selected);
      setRun(settled);
      await loadPeriods();
      await loadSettledDetail(selected);
      setMessage(`${fmtPeriod(settled.period_key)} settled.`);
    } catch (err) { setError(errMsg(err, "Failed to settle run")); }
    finally { setBusy(false); }
  };

  const handlePayout = async (party: RunPartyRollup, amount: number, reference: string, note: string) => {
    if (!selected) return;
    await recordPayout(orgId, selected, { party_id: party.party_id, amount, reference: reference || undefined, note: note || undefined });
    const summary = await getRun(orgId, selected);
    setRun(summary);
    await loadSettledDetail(selected);
    setMessage(`Payout recorded for ${party.party_name}.`);
  };

  const changeCadence = async (c: Cadence) => {
    if (c === cadence || busy) return;
    setBusy(true); setError(""); setMessage("");
    try {
      await setOrgCadence(orgId, c);
      setCadence(c);
      const ps = await loadPeriods();
      setSelected(null); setRun(null);
      setMessage(`Cadence set to ${c}. New periods will be ${c}; settled periods are unchanged. Delete a stale draft to re-slice it under ${c}.`);
      const pick = ps.find((p) => p.completed) || ps[0];
      if (pick) void openPeriod(pick.period_key, ps);
    } catch (err) { setError(errMsg(err, "Failed to change cadence")); }
    finally { setBusy(false); }
  };

  const handleDeleteDraft = async (periodKey: string) => {
    if (!window.confirm(`Delete the draft for ${fmtPeriod(periodKey)}? You can regenerate it.`)) return;
    setBusy(true); setError(""); setMessage("");
    try {
      await deleteDraftRun(orgId, periodKey);
      if (selected === periodKey) { setSelected(null); setRun(null); }
      await loadPeriods();
      setMessage(`Draft ${fmtPeriod(periodKey)} deleted.`);
    } catch (err) { setError(errMsg(err, "Failed to delete draft")); }
    finally { setBusy(false); }
  };

  const cadBtn = (c: Cadence): React.CSSProperties => ({
    ...ghostBtn, padding: "6px 14px",
    ...(cadence === c ? { background: "#2563eb", color: "#fff", borderColor: "#2563eb" } : {}),
  });

  return (
    <MaintenanceLayout title="Settlement · Runs">
      <Link to={`/maintenance/settlement/orgs/${orgId}`} style={{ ...ghostBtn, marginBottom: 14, width: "fit-content" }}>
        <ArrowLeft size={14} /> Back to {orgName || "org"} setup
      </Link>

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#333" }}>{orgName || "Organisation"} · settlements</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#777" }}>
              Generate a draft, then settle a completed period to lock it (immutable). A period can't be settled if it overlaps an already-settled one.
            </p>
          </div>
          {canEdit && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "#777", textTransform: "uppercase", letterSpacing: ".03em", fontWeight: 600, marginBottom: 5 }}>
                <CalendarClock size={12} style={{ verticalAlign: "-2px" }} /> Cadence
              </div>
              <div style={{ display: "inline-flex", gap: 6 }}>
                <button style={cadBtn("monthly")} onClick={() => changeCadence("monthly")} disabled={busy}>Monthly</button>
                <button style={cadBtn("quarterly")} onClick={() => changeCadence("quarterly")} disabled={busy}>Quarterly</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && <div style={{ ...card, borderColor: "#e0b4b4", background: "#fdecea", color: "#c0392b" }}>{error}</div>}
      {message && <div style={{ ...card, borderColor: "#bfe3cd", background: "#eaf7ef", color: "#1a7f4b" }}>{message}</div>}

      {loading ? (
        <p style={{ color: "#777" }}>Loading…</p>
      ) : (
        <>
          {/* Period list */}
          <div style={card}>
            <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600, color: "#333" }}>Periods ({cadence})</h3>
            {periods.length === 0 ? (
              <p style={{ color: "#777", margin: 0 }}>No charging activity at this org's stations yet.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
                  <thead>
                    <tr>
                      <th style={th}>Period</th><th style={th}>Range</th>
                      <th style={thR}>Sessions</th><th style={thR}>Energy</th>
                      <th style={thR}>Total payable</th><th style={th}>Status</th><th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {periods.map((p) => {
                      const isSel = p.period_key === selected;
                      return (
                        <tr key={p.period_key} style={isSel ? { background: "#f5f8ff" } : undefined}>
                          <td style={{ ...td, fontWeight: 700 }}>
                            {fmtPeriod(p.period_key)}
                            {p.cadence !== cadence && <span style={{ ...pillGrey, marginLeft: 6, fontSize: 10 }}>{p.cadence}</span>}
                          </td>
                          <td style={{ ...td, color: "#777" }}>{p.period_start} – {p.period_end}</td>
                          <td style={tdR}>{p.sessions.toLocaleString("en-IN")}</td>
                          <td style={tdR}>{num(p.kwh)} kWh</td>
                          <td style={tdR}>{p.status === "none" ? "—" : inr(p.total_payable)}</td>
                          <td style={td}>
                            {p.status === "settled" ? <span style={pillGreen}><Lock size={11} style={{ verticalAlign: "-1px" }} /> Settled</span>
                              : p.status === "draft" ? <span style={pillAmber}>Draft</span>
                              : !p.completed ? <span style={pillGrey}>In progress</span>
                              : <span style={pillBlue}>Not settled</span>}
                          </td>
                          <td style={td}>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <button style={ghostBtn} onClick={() => openPeriod(p.period_key)}>{isSel ? "Selected" : "Open"}</button>
                              {canEdit && p.status === "draft" && (
                                <button
                                  style={{ ...ghostBtn, color: "#c0392b", padding: "6px 8px" }}
                                  title="Delete draft (to re-slice under a changed cadence)"
                                  onClick={() => handleDeleteDraft(p.period_key)}
                                  disabled={busy}
                                ><Trash2 size={14} /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Selected period detail */}
          {selectedPeriod && (
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#333" }}>{fmtPeriod(selectedPeriod.period_key)}</h3>
                  <div style={{ fontSize: 13, color: "#777", marginTop: 2 }}>{selectedPeriod.period_start} – {selectedPeriod.period_end}</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {isSettled ? <span style={{ ...pillGreen, display: "inline-flex", alignItems: "center", gap: 5 }}><Lock size={13} /> Settled (immutable)</span>
                    : isDraft ? <span style={pillAmber}>Draft</span>
                    : !selectedPeriod.completed ? <span style={pillGrey}>In progress</span>
                    : <span style={pillBlue}>Not generated</span>}
                </div>
              </div>

              {run && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginTop: 12, fontSize: 13 }}>
                  <span style={{ color: "#555" }}><Clock size={13} style={{ verticalAlign: "-2px" }} /> Computed by <b>{run.computed_by || "—"}</b></span>
                  {isSettled && <span style={{ color: "#555" }}><CheckCircle size={13} style={{ verticalAlign: "-2px" }} /> Settled by <b>{run.settled_by || "—"}</b> on <b>{fmtDate(run.settled_at)}</b></span>}
                </div>
              )}

              {canEdit && (
                <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                  {!isSettled && (
                    <button style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }} onClick={compute} disabled={busy}>
                      <RefreshCw size={16} /> {busy ? "Computing…" : run ? "Recompute draft" : "Generate draft"}
                    </button>
                  )}
                  {isDraft && (
                    selectedPeriod.completed ? (
                      <button style={{ ...dangerBtn, opacity: busy ? 0.6 : 1 }} onClick={handleSettle} disabled={busy}>
                        <Lock size={16} /> Settle (lock period)
                      </button>
                    ) : (
                      <span style={{ ...pillGrey, display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <AlertTriangle size={13} /> Can settle after the period ends ({selectedPeriod.period_end})
                      </span>
                    )
                  )}
                </div>
              )}
              {run && selected && (
                <div style={{ marginTop: 12 }}>
                  <Link
                    to={`/maintenance/settlement/orgs/${orgId}/runs/${encodeURIComponent(selected)}/report`}
                    style={{ ...ghostBtn, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content" }}
                  >
                    <FileText size={14} /> View / print statement
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Run figures */}
          {run && (
            <>
              {isSettled && (
                <div style={{ ...card, borderLeft: "4px solid #1a7f4b", background: "#eaf7ef", color: "#1a5f3a", display: "flex", alignItems: "center", gap: 8 }}>
                  <Lock size={18} color="#1a7f4b" />
                  <span style={{ fontWeight: 600 }}>This period is settled — figures are frozen and auditable. Payouts are recorded separately.</span>
                </div>
              )}

              {run.uncovered.kwh > 0 && (
                <div style={{ ...card, borderLeft: "4px solid #b9770e", background: "#fff7e6", color: "#8a5a0e" }}>
                  <b>Uncovered:</b> {num(run.uncovered.kwh)} kWh across {run.uncovered.sessions.toLocaleString("en-IN")} sessions not attributed (stations without a settlement profile / device-rate). Not included in payouts.
                </div>
              )}

              {/* Party rollup */}
              <div style={card}>
                <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600, color: "#333" }}>
                  <IndianRupee size={16} style={{ verticalAlign: "-2px" }} /> Party rollup
                </h3>
                {run.parties.length === 0 ? <p style={{ color: "#777", margin: 0 }}>No parties in this run (no rate-card profiles for the priced stations).</p> : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
                      <thead>
                        <tr>
                          <th style={th}>Party</th><th style={thR}>Consumables (reimb.)</th><th style={thR}>Profit share</th>
                          <th style={thR}>GST</th><th style={thR}>Total payable</th><th style={thR}>Paid</th><th style={th}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {run.parties.map((p) => (
                          <tr key={p.party_id}>
                            <td style={td}><span style={{ fontWeight: 600 }}>{p.party_name}</span> <span style={{ color: "#999", fontSize: 12 }}>({p.party_type})</span></td>
                            <td style={tdR}>{inr(p.reimbursement)}</td>
                            <td style={tdR}>{inr(p.share)}</td>
                            <td style={tdR}>{inr(p.gst)}</td>
                            <td style={{ ...tdR, fontWeight: 700 }}>{inr(p.total_payable)}</td>
                            <td style={tdR}>{inr(p.paid)}</td>
                            <td style={td}>{p.fully_paid ? <span style={pillGreen}>Paid</span> : <span style={pillAmber}>Accrued</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <TierBreakdown profiles={run.profiles} />

              {/* GST ledger */}
              <div style={card}>
                <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600, color: "#333" }}>GST ledger</h3>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14, fontSize: 15 }}>
                  <span>Output GST <b>{inr(run.gst_ledger.output_gst)}</b></span>
                  <span style={{ color: "#999" }}>−</span>
                  <span>Input credit <b>{inr(run.gst_ledger.input_credit)}</b></span>
                  <span style={{ color: "#999" }}>=</span>
                  <span style={{ ...pillBlue, fontSize: 15, padding: "4px 12px" }}>Net payable {inr(run.gst_ledger.net_payable)}</span>
                </div>
              </div>

              {/* Settled-only: payouts + history + audit */}
              {isSettled && (
                <>
                  {canEdit && (
                    <div style={card}>
                      <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600, color: "#333" }}>Record payouts</h3>
                      {run.parties.filter((p) => !p.fully_paid).length === 0 ? <p style={{ color: "#1a7f4b", margin: 0 }}>All parties fully paid.</p> : (
                        <table className="maint-stack" style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
                          <thead><tr><th style={th}>Party</th><th style={thR}>Due</th><th style={th}>Record payout</th></tr></thead>
                          <tbody>
                            {run.parties.map((p) => (
                              <tr key={p.party_id}>
                                <td data-label="Party" style={td}><span style={{ fontWeight: 600 }}>{p.party_name}</span></td>
                                <td data-label="Due" style={tdR}>{inr(Math.max(0, p.total_payable - p.paid))}</td>
                                <td data-label="Record payout" style={td}><PayoutForm party={p} onSubmit={(a, r, n) => handlePayout(p, a, r, n)} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  <div style={card}>
                    <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600, color: "#333" }}>Payout history</h3>
                    {payouts.length === 0 ? <p style={{ color: "#777", margin: 0 }}>No payouts recorded yet.</p> : (
                      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
                        <thead><tr><th style={th}>Party</th><th style={thR}>Amount</th><th style={th}>Reference</th><th style={th}>Paid by</th><th style={th}>Paid on</th></tr></thead>
                        <tbody>
                          {payouts.map((p) => (
                            <tr key={p.id}><td style={td}>{p.party_name}</td><td style={tdR}>{inr(p.amount)}</td><td style={td}>{p.reference || "—"}</td><td style={td}>{p.paid_by || "—"}</td><td style={td}>{fmtDate(p.paid_on)}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  <div style={card}>
                    <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600, color: "#333" }}>Audit trail</h3>
                    {audit.length === 0 ? <p style={{ color: "#777", margin: 0 }}>No audit entries.</p> : (
                      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                        {audit.map((a, i) => (
                          <li key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f0f0f0", fontSize: 13 }}>
                            <span style={pillGrey}>{a.action}</span>
                            <span style={{ color: "#555" }}>{a.actor || "—"}</span>
                            <span style={{ color: "#999", marginLeft: "auto" }}>{fmtDate(a.at)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </MaintenanceLayout>
  );
};

export default SettlementOrgRuns;
